import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, TextField, Button, Snackbar, Alert,
  FormControlLabel, Switch, CircularProgress, Divider, Chip,
} from '@mui/material';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import { type Api, type TenantView } from './api';

const URL_RE = /^https?:\/\/.+/i;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const badUrl = (u: string) => u.trim() !== '' && !URL_RE.test(u.trim());
const badEmail = (e: string) => e.trim() !== '' && !EMAIL_RE.test(e.trim());

export function TenantSetup({ api, tenant }: { api: Api; tenant: TenantView }) {
  const isSuper = tenant.role === 'SUPER_ADMIN';
  const [loaded, setLoaded] = useState(false);
  const [marketingUrl, setMarketingUrl] = useState('');
  const [docsUrl, setDocsUrl] = useState('');
  const [docsEnabled, setDocsEnabled] = useState(true);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [newRecipient, setNewRecipient] = useState('');
  const [leadsScoped, setLeadsScoped] = useState(false);
  const [leadSubject, setLeadSubject] = useState('');
  const [salesEmail, setSalesEmail] = useState('');
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  useEffect(() => {
    let alive = true;
    setLoaded(false);
    api.tenantConfig(tenant.tenantId)
      .then((c) => {
        if (!alive) return;
        setMarketingUrl(c.marketingUrl ?? '');
        setDocsUrl(c.docsUrl ?? '');
        setDocsEnabled(c.docsEnabled);
        setRecipients(c.leadRecipients ?? []);
        setLeadsScoped(c.leadsScoped);
        setLeadSubject(c.leadSubject ?? '');
        setSalesEmail(c.salesEmail ?? '');
        setCalendlyUrl(c.calendlyUrl ?? '');
        setLoaded(true);
      })
      .catch(() => { if (alive) { setToast({ msg: 'Failed to load settings', sev: 'error' }); setLoaded(true); } });
    return () => { alive = false; };
  }, [tenant.tenantId]);

  function addRecipient() {
    const e = newRecipient.trim().toLowerCase();
    if (!e) return;
    if (!EMAIL_RE.test(e)) { setToast({ msg: 'Enter a valid email', sev: 'error' }); return; }
    if (!recipients.includes(e)) setRecipients([...recipients, e]);
    setNewRecipient('');
  }

  async function save() {
    if (badUrl(marketingUrl) || badUrl(docsUrl) || badUrl(calendlyUrl)) {
      setToast({ msg: 'Enter valid http(s) URLs', sev: 'error' }); return;
    }
    if (badEmail(salesEmail)) { setToast({ msg: 'Enter a valid sales email', sev: 'error' }); return; }
    setBusy(true);
    try {
      await api.saveTenantConfig(tenant.tenantId, {
        marketingUrl: marketingUrl.trim() || null,
        docsUrl: docsUrl.trim() || null,
        docsEnabled,
        leadRecipients: recipients,
        leadsScoped,
        leadSubject: leadSubject.trim() || null,
        salesEmail: salesEmail.trim() || null,
        calendlyUrl: calendlyUrl.trim() || null,
      });
      setToast({ msg: 'Tenant settings saved', sev: 'success' });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Save failed', sev: 'error' });
    } finally {
      setBusy(false);
    }
  }

  const section = { p: 3, mb: 2 } as const;
  const ro = !isSuper || busy;

  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <TuneOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Tenant Setup</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Per-tenant settings for <b>{tenant.name}</b>, managed here instead of environment files. Secrets (SMTP, IdP, keys) stay in the server environment and never appear here.
      </Typography>

      {!isSuper && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          These settings are read-only for your role — only a <b>Super Admin</b> can change them.
        </Alert>
      )}

      {!loaded ? (
        <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : (
        <>
          {/* Public URLs */}
          <Paper variant="outlined" sx={section}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Public URLs</Typography>
            <Typography variant="caption" color="text.secondary">
              Where this tenant's live sites are hosted. Used by the “View live” buttons. Full origin, e.g. <code>https://cymmetri.com</code>.
            </Typography>
            <Stack spacing={2.5} sx={{ mt: 2 }}>
              <TextField label="Marketing site URL" fullWidth size="small" placeholder="https://cymmetri.com"
                value={marketingUrl} onChange={(e) => setMarketingUrl(e.target.value)} disabled={ro}
                error={badUrl(marketingUrl)} helperText={badUrl(marketingUrl) ? 'Must start with http:// or https://' : ' '} />
              <TextField label="Docs site URL" fullWidth size="small" placeholder="https://docs.cymmetri.com"
                value={docsUrl} onChange={(e) => setDocsUrl(e.target.value)} disabled={ro}
                error={badUrl(docsUrl)} helperText={badUrl(docsUrl) ? 'Must start with http:// or https://' : ' '} />
              <FormControlLabel control={<Switch checked={docsEnabled} onChange={(e) => setDocsEnabled(e.target.checked)} disabled={ro} />}
                label="Docs site enabled (show “View live” on the docs editor)" />
            </Stack>
          </Paper>

          {/* Lead settings */}
          <Paper variant="outlined" sx={section}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Lead settings</Typography>
            <Typography variant="caption" color="text.secondary">
              Who can receive a forwarded lead from the Leads dashboard. Leave empty to use the built-in default recipients.
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {recipients.length === 0 && <Typography variant="body2" color="text.secondary">Using built-in defaults.</Typography>}
              {recipients.map((r) => (
                <Chip key={r} label={r} onDelete={ro ? undefined : () => setRecipients(recipients.filter((x) => x !== r))} />
              ))}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <TextField size="small" placeholder="add sales recipient email" value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)} disabled={ro}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }} sx={{ flex: 1 }} />
              <Button startIcon={<AddIcon />} onClick={addRecipient} disabled={ro || !newRecipient.trim()}>Add</Button>
            </Stack>
            <TextField
              label="Send-Lead email subject" fullWidth size="small" sx={{ mt: 2.5 }}
              value={leadSubject} onChange={(e) => setLeadSubject(e.target.value)} disabled={ro}
              placeholder="{tenant}: a new lead is submitted"
              helperText="Tokens: {tenant} {name} {company} {email}. Blank = default “{tenant}: a new lead is submitted”."
            />
            <Divider sx={{ my: 2 }} />
            <FormControlLabel control={<Switch checked={leadsScoped} onChange={(e) => setLeadsScoped(e.target.checked)} disabled={ro} />}
              label="Confine leads to this tenant (non-super-admins see only leads from this tenant's sites)" />
          </Paper>

          {/* Contact & booking */}
          <Paper variant="outlined" sx={section}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Contact &amp; booking</Typography>
            <Typography variant="caption" color="text.secondary">
              Primary sales contact + booking link for this tenant.
            </Typography>
            <Stack spacing={2.5} sx={{ mt: 2 }}>
              <TextField label="Sales email" fullWidth size="small" placeholder="sales@cymmetri.com"
                value={salesEmail} onChange={(e) => setSalesEmail(e.target.value)} disabled={ro}
                error={badEmail(salesEmail)} helperText={badEmail(salesEmail) ? 'Enter a valid email' : ' '} />
              <TextField label="Calendly / booking URL" fullWidth size="small" placeholder="https://calendly.com/…"
                value={calendlyUrl} onChange={(e) => setCalendlyUrl(e.target.value)} disabled={ro}
                error={badUrl(calendlyUrl)} helperText={badUrl(calendlyUrl) ? 'Must start with http:// or https://' : ' '} />
            </Stack>
          </Paper>

          <Stack direction="row" justifyContent="flex-end">
            <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={ro}>
              {busy ? 'Saving…' : 'Save settings'}
            </Button>
          </Stack>
        </>
      )}

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)} variant="filled">{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
